import { useState, useRef, useEffect } from 'react';
import { Plane, Calendar, ArrowLeftRight, ArrowRight, X } from 'lucide-react';
import type { Airport, FlightType, UpdateFlightInput, Flight } from '@/types';
import { useAirportSearch } from '@/hooks';

interface EditFlightFormProps {
  flight: Flight;
  onUpdateFlight: (flightId: string, input: UpdateFlightInput) => Promise<void>;
  onCancel: () => void;
}

export function EditFlightForm({ flight, onUpdateFlight, onCancel }: EditFlightFormProps) {
  const fromSearch = useAirportSearch();
  const toSearch = useAirportSearch();
  const [flightDate, setFlightDate] = useState(flight.flightDate);
  const [flightType, setFlightType] = useState<FlightType>(flight.flightType);
  const [notes, setNotes] = useState(flight.notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFromResults, setShowFromResults] = useState(false);
  const [showToResults, setShowToResults] = useState(false);

  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);

  // Initialize the form with existing flight data
  useEffect(() => {
    fromSearch.setSelectedAirport(flight.fromAirport);
    fromSearch.setQuery(`${flight.fromAirport.iata} - ${flight.fromAirport.city}`);
    toSearch.setSelectedAirport(flight.toAirport);
    toSearch.setQuery(`${flight.toAirport.iata} - ${flight.toAirport.city}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fromRef.current && !fromRef.current.contains(event.target as Node)) {
        setShowFromResults(false);
      }
      if (toRef.current && !toRef.current.contains(event.target as Node)) {
        setShowToResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectAirport = (
    airport: Airport,
    type: 'from' | 'to'
  ) => {
    if (type === 'from') {
      fromSearch.setSelectedAirport(airport);
      fromSearch.setQuery(`${airport.iata} - ${airport.city}`);
      setShowFromResults(false);
    } else {
      toSearch.setSelectedAirport(airport);
      toSearch.setQuery(`${airport.iata} - ${airport.city}`);
      setShowToResults(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fromSearch.selectedAirport || !toSearch.selectedAirport) return;

    setIsSubmitting(true);
    try {
      await onUpdateFlight(flight.id, {
        fromAirport: fromSearch.selectedAirport,
        toAirport: toSearch.selectedAirport,
        flightDate,
        flightType,
        notes: notes.trim() || undefined,
      });
      onCancel(); // Close the form after successful update
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    fromSearch.selectedAirport &&
    toSearch.selectedAirport &&
    flightDate &&
    !isSubmitting;

  return (
    <div className="glass rounded-xl md:rounded-2xl p-4 md:p-6 glow-box">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base md:text-lg font-semibold flex items-center gap-2">
          <Plane className="w-4 h-4 md:w-5 md:h-5 text-primary-500" />
          Edit Flight
        </h3>
        <button
          onClick={onCancel}
          className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-all"
          title="Cancel editing"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
        {/* From Airport */}
        <div ref={fromRef} className="relative">
          <label className="block text-xs md:text-sm text-white/70 mb-1">From</label>
          <input
            type="text"
            value={fromSearch.query}
            onChange={(e) => {
              fromSearch.setQuery(e.target.value);
              fromSearch.setSelectedAirport(null);
              setShowFromResults(true);
            }}
            onFocus={() => setShowFromResults(true)}
            placeholder="Search airport or city..."
            className="w-full text-sm md:text-base"
          />
          {fromSearch.selectedAirport && (
            <button
              type="button"
              onClick={() => fromSearch.clearSearch()}
              className="absolute right-3 top-8 text-white/50 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Search results dropdown */}
          {showFromResults && fromSearch.results.length > 0 && (
            <div className="search-results flight-scroll mobile-scroll visible">
              {fromSearch.results.map((airport) => (
                <div
                  key={airport.iata}
                  className="search-result"
                  onClick={() => handleSelectAirport(airport, 'from')}
                >
                  <span className="result-code">{airport.iata}</span>
                  <span className="result-info">
                    <span className="result-name">{airport.city}</span>
                    <span className="result-country">{airport.country}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Flight direction indicator */}
        <div className="flex justify-center">
          <div className="text-2xl text-primary-500">
            {flightType === 'return' ? (
              <ArrowLeftRight className="w-6 h-6" />
            ) : (
              <ArrowRight className="w-6 h-6" />
            )}
          </div>
        </div>

        {/* To Airport */}
        <div ref={toRef} className="relative">
          <label className="block text-sm text-white/70 mb-1">To</label>
          <input
            type="text"
            value={toSearch.query}
            onChange={(e) => {
              toSearch.setQuery(e.target.value);
              toSearch.setSelectedAirport(null);
              setShowToResults(true);
            }}
            onFocus={() => setShowToResults(true)}
            placeholder="Search airport or city..."
            className="w-full"
          />
          {toSearch.selectedAirport && (
            <button
              type="button"
              onClick={() => toSearch.clearSearch()}
              className="absolute right-3 top-8 text-white/50 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Search results dropdown */}
          {showToResults && toSearch.results.length > 0 && (
            <div className="search-results flight-scroll visible">
              {toSearch.results.map((airport) => (
                <div
                  key={airport.iata}
                  className="search-result"
                  onClick={() => handleSelectAirport(airport, 'to')}
                >
                  <span className="result-code">{airport.iata}</span>
                  <span className="result-info">
                    <span className="result-name">{airport.city}</span>
                    <span className="result-country">{airport.country}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Flight Type Toggle */}
        <div>
          <label className="block text-xs md:text-sm text-white/70 mb-2">Flight Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFlightType('direct')}
              className={`flex-1 py-2 px-3 md:px-4 rounded-xl transition-all flex items-center justify-center gap-2 text-sm md:text-base ${
                flightType === 'direct'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              <ArrowRight className="w-4 h-4" />
              <span className="hidden sm:inline">One Way</span>
              <span className="sm:hidden">One</span>
            </button>
            <button
              type="button"
              onClick={() => setFlightType('return')}
              className={`flex-1 py-2 px-3 md:px-4 rounded-xl transition-all flex items-center justify-center gap-2 text-sm md:text-base ${
                flightType === 'return'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              <ArrowLeftRight className="w-4 h-4" />
              Return
            </button>
          </div>
          <p className="text-xs text-white/50 mt-1">
            {flightType === 'return'
              ? 'Return flights count as 2 flights in your stats'
              : 'One way flight counts as 1 flight'}
          </p>
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm text-white/70 mb-1 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Date
          </label>
          <input
            type="date"
            value={flightDate}
            onChange={(e) => setFlightDate(e.target.value)}
            className="w-full"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm text-white/70 mb-1">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Family trip"
            className="w-full min-h-[80px] resize-y"
            maxLength={500}
          />
          {notes.length > 0 && (
            <p className="text-xs text-white/50 mt-1">
              {notes.length}/500 characters
            </p>
          )}
        </div>

        {/* Submit button */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2 px-3 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 transition-all text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex-1 btn-primary flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm"
          >
            <Plane className="w-4 h-4" />
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
